// Tries — Basic vs Optimized code for every "Beginner Problems" drill listed in the Tries
// Explanation modal. Same shape as treeDrills.js: concise steps + an interactive "watch it
// happen" visualizer per version, using ApproachVisualizer's trie-rendering support. Three
// drills cross-reference the curated problems instead of duplicating content.

const trieDrills = [
  {
    id: 'build-trie-node',
    title: 'Design the TrieNode Class',
    level: 'Easy',
    versions: [
      {
        name: 'Basic — A HashMap of Children',
        explanation: 'Store each node\'s children in a Map<Character, TrieNode>. Flexible — it works for any alphabet (letters, digits, even emoji) — but every lookup pays a hashing cost, and each node carries the overhead of an actual HashMap object even when it only has one or two children.',
        steps: [
          'Give every node a Map<Character, TrieNode> for its children.',
          'Give every node a boolean isEnd flag, marking whether a complete word ends here.',
          'To move to a child, look it up in the map by character.',
          'To add a child, insert it into the map — works for any character the map can hold.',
        ],
        code:
`class TrieNode {
    Map<Character, TrieNode> children = new HashMap<>();
    boolean isEnd = false;
}`,
        complexity: { time: 'O(1) average per child lookup', space: 'O(1) node overhead, plus HashMap bucket overhead per node' },
      },
      {
        name: 'Optimized — A Fixed-Size Array of Children',
        explanation: 'If the alphabet is known and small (lowercase English letters, say), replace the HashMap with a plain TrieNode[26] array, indexed directly by c - \'a\'. No hashing at all — just address arithmetic, the same reason arr[i] beats a map lookup when it\'s available.',
        steps: [
          'Give every node a fixed-size array, TrieNode[26], one slot per possible letter.',
          'To move to a child for character c, index directly: children[c - \'a\'].',
          'A null slot means that character was never inserted at this position.',
          'Trades flexibility (only works for the fixed alphabet) for raw speed and simpler memory layout.',
        ],
        code:
`class TrieNode {
    TrieNode[] children = new TrieNode[26];   // one slot per lowercase letter
    boolean isEnd = false;
}

// Access pattern:
TrieNode child = node.children[c - 'a'];`,
        complexity: { time: 'O(1) per child lookup, no hashing', space: 'O(26) per node, fixed — can be wasteful for very sparse tries' },
      },
    ],
  },
  {
    id: 'count-words-with-prefix',
    title: 'Count How Many Inserted Words Share a Given Prefix',
    level: 'Easy',
    versions: [
      {
        name: 'Basic — Collect Every Word, Then Filter',
        explanation: 'Walk the entire trie collecting every single word that was ever inserted, then count how many of them start with the target prefix. Correct, but visits every word in the trie even ones nowhere near the prefix in question.',
        steps: [
          'DFS the whole trie from the root, collecting every complete word into a list.',
          'Walk that list, checking each word against the target prefix with startsWith().',
          'Count the matches.',
          'Every word gets visited, including branches that share nothing with the prefix.',
        ],
        code:
`int countWithPrefixBasic(TrieNode root, String prefix) {
    List<String> allWords = new ArrayList<>();
    collectAll(root, "", allWords);
    int count = 0;
    for (String word : allWords) {
        if (word.startsWith(prefix)) count++;
    }
    return count;
}

private void collectAll(TrieNode node, String path, List<String> words) {
    if (node.isEnd) words.add(path);
    for (var entry : node.children.entrySet()) {
        collectAll(entry.getValue(), path + entry.getKey(), words);
    }
}`,
        complexity: { time: 'O(total characters across every inserted word)', space: 'O(number of words)' },
      },
      {
        name: 'Optimized — Walk to the Prefix Node, Then Count Only That Subtree',
        explanation: 'Follow the prefix down to its exact node in the trie — a direct walk, no branching decisions needed. Every word sharing that prefix lives EXCLUSIVELY in the subtree below that one node — count end-of-word markers there, and nowhere else in the trie is ever touched.',
        steps: [
          'Walk down the trie one character at a time, following the prefix exactly.',
          'If the walk falls off the trie before finishing the prefix, no words share it — return 0.',
          'Otherwise, DFS only the subtree rooted at that node, counting isEnd markers.',
          'Branches unrelated to the prefix (like an entirely different starting letter) are never visited.',
        ],
        visual: {
          trie: { label: 'trie (words: cat, car, card, care, dog)', value: {
            children: {
              c: { children: { a: { children: {
                t: { children: {}, isEnd: true },
                r: { children: {
                  d: { children: {}, isEnd: true },
                  e: { children: {}, isEnd: true },
                }, isEnd: true },
              }, isEnd: false } }, isEnd: false },
              d: { children: { o: { children: { g: { children: {}, isEnd: true } }, isEnd: false } }, isEnd: false },
            }, isEnd: false,
          } },
          varNames: ['count'],
          steps: [
            { highlights: { trie: ['c', 'ca'] }, vars: { count: 0 }, note: 'Walk directly to the "ca" node — 2 hops, no other branches touched.' },
            { highlights: { trie: ['cat'] }, vars: { count: 1 }, note: '"cat" is a complete word — count = 1.' },
            { highlights: { trie: ['car'] }, vars: { count: 2 }, note: '"car" is also a complete word — count = 2.' },
            { highlights: { trie: ['card', 'care'] }, vars: { count: 4 }, note: '"card" and "care" both complete words too — count = 4. The "dog" branch was never visited at all.' },
          ],
        },
        code:
`int countWithPrefix(TrieNode root, String prefix) {
    TrieNode node = root;
    for (char c : prefix.toCharArray()) {
        node = node.children.get(c);
        if (node == null) return 0;   // prefix doesn't exist at all
    }
    return countWords(node);
}

private int countWords(TrieNode node) {
    int count = node.isEnd ? 1 : 0;
    for (TrieNode child : node.children.values()) {
        count += countWords(child);
    }
    return count;
}`,
        complexity: { time: 'O(prefix length + size of the matching subtree)', space: 'O(h) — recursion depth' },
      },
    ],
  },
  {
    id: 'trie-search-drill',
    title: 'Insert and Search in a Trie',
    level: 'Easy',
    crossReference:
      "This drill's full Basic vs. Optimized walkthrough lives in this pattern's curated problem list — open " +
      "\"Implement Trie (Prefix Tree)\" there for the complete step-by-step and both code versions. You can also " +
      "try insert/search/startsWith hands-on in the \"Build & Insert\" and \"Search & StartsWith\" modes of the " +
      "Interactive Practice playground.",
  },
  {
    id: 'longest-common-prefix-using-trie',
    title: 'Longest Common Prefix Using a Trie',
    level: 'Medium',
    versions: [
      {
        name: 'Basic — Sort, Then Compare Only the First and Last Strings',
        explanation: 'Sort the array of strings alphabetically. The two strings that differ the MOST from each other end up at opposite ends after sorting — so the common prefix of the entire array must be at least as long as the common prefix of just those two.',
        steps: [
          'Sort the array of strings alphabetically.',
          'Compare only the first and last strings after sorting, character by character.',
          'Stop at the first mismatch (or the end of the shorter string).',
          'Everything matched so far is the longest common prefix of the whole array.',
        ],
        code:
`String longestCommonPrefixBasic(String[] words) {
    if (words.length == 0) return "";
    Arrays.sort(words);
    String first = words[0], last = words[words.length - 1];
    int i = 0;
    while (i < first.length() && i < last.length() && first.charAt(i) == last.charAt(i)) {
        i++;
    }
    return first.substring(0, i);
}`,
        complexity: { time: 'O(n log n · k) — sorting dominates, k = average string length', space: 'O(n · k) — for the sort' },
      },
      {
        name: 'Optimized — Build a Trie, Walk Down While There\'s Only One Path',
        explanation: 'Insert every word into a trie. The longest common prefix is exactly the path from the root that stays "unambiguous" — as long as a node has exactly one child AND isn\'t itself the end of some shorter word, every inserted word is still forced through that single path.',
        steps: [
          'Insert every word into a trie.',
          'Starting at the root, walk down as long as the current node has exactly one child.',
          'Stop the moment a node has more than one child (the words diverge here) or is the end of a word (a shorter word stops here).',
          'The characters walked through form the longest common prefix.',
        ],
        visual: {
          trie: { label: 'trie (words: flower, flow, flight)', value: {
            children: { f: { children: { l: { children: {
              o: { children: { w: { children: {
                e: { children: { r: { children: {}, isEnd: true } }, isEnd: false },
              }, isEnd: true } }, isEnd: false },
              i: { children: { g: { children: { h: { children: { t: { children: {}, isEnd: true } }, isEnd: false } }, isEnd: false } }, isEnd: false },
            }, isEnd: false } }, isEnd: false } }, isEnd: false,
          } },
          varNames: [],
          steps: [
            { highlights: { trie: ['f'] }, note: 'Root has exactly 1 child (\'f\') — keep walking.' },
            { highlights: { trie: ['fl'] }, note: '\'f\' has exactly 1 child (\'l\') — keep walking.' },
            { highlights: { trie: ['fl'] }, note: '\'l\' has 2 children (\'o\' for flow/flower, \'i\' for flight) — stop here. Longest common prefix: "fl".' },
          ],
        },
        code:
`String longestCommonPrefix(String[] words) {
    TrieNode root = new TrieNode();
    for (String word : words) insert(root, word);

    StringBuilder prefix = new StringBuilder();
    TrieNode node = root;
    while (node.children.size() == 1 && !node.isEnd) {
        char onlyChar = node.children.keySet().iterator().next();
        prefix.append(onlyChar);
        node = node.children.get(onlyChar);
    }
    return prefix.toString();
}`,
        complexity: { time: 'O(total characters across all words)', space: 'O(total characters) — the trie itself' },
      },
    ],
  },
  {
    id: 'wildcard-search-drill',
    title: 'Wildcard Search With "." in a Trie',
    level: 'Medium',
    crossReference:
      "This drill's full Basic vs. Optimized walkthrough lives in this pattern's curated problem list — open " +
      "\"Design Add and Search Words Data Structure\" there for the complete step-by-step and both code versions. " +
      "You can also try it hands-on in the \"Wildcard Search\" mode of the Interactive Practice playground.",
  },
  {
    id: 'autocomplete-suggestions',
    title: 'Autocomplete: Suggest Every Word Starting With a Prefix',
    level: 'Medium',
    versions: [
      {
        name: 'Basic — Collect Every Word, Then Filter',
        explanation: 'Same shape as the naive prefix-count drill: gather every word in the whole trie, then keep only the ones that start with the target prefix. Correct, but scans the entire trie regardless of how few (or many) words actually match.',
        steps: [
          'DFS the whole trie, collecting every complete word into a list.',
          'Filter that list down to only the words starting with the target prefix.',
          'Return the filtered list.',
          'Words with nothing in common with the prefix still get visited and discarded.',
        ],
        code:
`List<String> autocompleteBasic(TrieNode root, String prefix) {
    List<String> allWords = new ArrayList<>();
    collectAll(root, "", allWords);
    List<String> result = new ArrayList<>();
    for (String word : allWords) {
        if (word.startsWith(prefix)) result.add(word);
    }
    return result;
}

private void collectAll(TrieNode node, String path, List<String> words) {
    if (node.isEnd) words.add(path);
    for (var entry : node.children.entrySet()) {
        collectAll(entry.getValue(), path + entry.getKey(), words);
    }
}`,
        complexity: { time: 'O(total characters across every inserted word)', space: 'O(number of words)' },
      },
      {
        name: 'Optimized — Walk to the Prefix, Then Collect Only That Subtree',
        explanation: 'Follow the prefix down to its node directly, then DFS only from there — every suggestion worth returning lives in that one subtree, and nothing outside it is ever visited.',
        steps: [
          'Walk down the trie following the prefix exactly, one character at a time.',
          'If the walk falls off before the prefix finishes, there are no suggestions — return an empty list.',
          'Otherwise, DFS the subtree rooted there, collecting every complete word (prefix + whatever comes after).',
          'Return that list — nothing outside the prefix\'s subtree was ever touched.',
        ],
        visual: {
          trie: { label: 'trie (words: cat, car, card, care, dog)', value: {
            children: {
              c: { children: { a: { children: {
                t: { children: {}, isEnd: true },
                r: { children: {
                  d: { children: {}, isEnd: true },
                  e: { children: {}, isEnd: true },
                }, isEnd: true },
              }, isEnd: false } }, isEnd: false },
              d: { children: { o: { children: { g: { children: {}, isEnd: true } }, isEnd: false } }, isEnd: false },
            }, isEnd: false,
          } },
          varNames: [],
          steps: [
            { highlights: { trie: ['c', 'ca', 'car'] }, note: 'Walk directly to the "car" node — 3 hops.' },
            { highlights: { trie: ['car'] }, note: '"car" itself is a complete word — suggest it.' },
            { highlights: { trie: ['card', 'care'] }, note: 'DFS from "car": "card" and "care" are both complete words in this subtree — suggest both. Final suggestions: [car, card, care]. "cat" and "dog" were never visited — they don\'t share the "car" prefix.' },
          ],
        },
        code:
`List<String> autocomplete(TrieNode root, String prefix) {
    TrieNode node = root;
    for (char c : prefix.toCharArray()) {
        node = node.children.get(c);
        if (node == null) return new ArrayList<>();
    }
    List<String> result = new ArrayList<>();
    collect(node, prefix, result);
    return result;
}

private void collect(TrieNode node, String path, List<String> result) {
    if (node.isEnd) result.add(path);
    for (var entry : node.children.entrySet()) {
        collect(entry.getValue(), path + entry.getKey(), result);
    }
}`,
        complexity: { time: 'O(prefix length + size of the matching subtree)', space: 'O(h) — recursion depth' },
      },
    ],
  },
  {
    id: 'replace-words-with-root',
    title: 'Replace Words With Their Shortest Dictionary Root',
    level: 'Advanced',
    versions: [
      {
        name: 'Basic — Check Every Root Against Every Word',
        explanation: 'For each word in the sentence, check it against every root in the dictionary using startsWith(), keeping the shortest one that matches. Correct, but every word triggers a full scan of the entire root dictionary.',
        steps: [
          'For each word in the sentence, check it against every root in the dictionary.',
          'A root matches if the word starts with it.',
          'Keep the shortest matching root found (or the original word, if none match).',
          'Every word costs O(number of roots × average root length) — adds up for a large dictionary.',
        ],
        code:
`String replaceWordsBasic(List<String> roots, String sentence) {
    String[] words = sentence.split(" ");
    for (int i = 0; i < words.length; i++) {
        String shortest = null;
        for (String root : roots) {
            if (words[i].startsWith(root) && (shortest == null || root.length() < shortest.length())) {
                shortest = root;
            }
        }
        if (shortest != null) words[i] = shortest;
    }
    return String.join(" ", words);
}`,
        complexity: { time: 'O(words · roots · avgRootLength)', space: 'O(sentence length)' },
      },
      {
        name: 'Optimized: Trie of Roots, Walk Each Word Until the First End-of-Root',
        explanation: 'Insert every root into a trie once. For each word, walk down the trie following the word\'s own characters — the INSTANT an end-of-word (isEnd) marker is reached, that\'s the shortest matching root, and the walk can stop immediately without looking at the rest of the word.',
        steps: [
          'Build a trie from all the dictionary roots, once.',
          'For each word in the sentence, walk the trie one character at a time, following the word.',
          'The moment an isEnd node is reached, that prefix IS the shortest root — stop and use it.',
          'If the walk falls off the trie before hitting an isEnd node, no root matches — keep the original word.',
        ],
        visual: {
          trie: { label: 'trie of roots (cat, bat, rat)', value: {
            children: {
              c: { children: { a: { children: { t: { children: {}, isEnd: true } }, isEnd: false } }, isEnd: false },
              b: { children: { a: { children: { t: { children: {}, isEnd: true } }, isEnd: false } }, isEnd: false },
              r: { children: { a: { children: { t: { children: {}, isEnd: true } }, isEnd: false } }, isEnd: false },
            }, isEnd: false,
          } },
          varNames: [],
          steps: [
            { highlights: { trie: ['c'] }, note: 'Word "cattle": follow \'c\' — exists, continue.' },
            { highlights: { trie: ['c', 'ca'] }, note: 'Follow \'a\' — exists, continue.' },
            { highlights: { trie: ['cat'] }, note: 'Follow \'t\' — exists, AND this node is isEnd! Stop immediately: shortest root is "cat" — the remaining letters "tle" are never even looked at. Replace "cattle" → "cat".' },
          ],
        },
        code:
`String replaceWords(List<String> roots, String sentence) {
    TrieNode trieRoot = new TrieNode();
    for (String root : roots) insert(trieRoot, root);

    String[] words = sentence.split(" ");
    for (int i = 0; i < words.length; i++) {
        words[i] = findShortestRoot(trieRoot, words[i]);
    }
    return String.join(" ", words);
}

private String findShortestRoot(TrieNode trieRoot, String word) {
    TrieNode node = trieRoot;
    for (int i = 0; i < word.length(); i++) {
        char c = word.charAt(i);
        if (node.children.get(c) == null) return word;   // no root matches
        node = node.children.get(c);
        if (node.isEnd) return word.substring(0, i + 1);   // shortest root found
    }
    return word;
}`,
        complexity: { time: 'O(total root characters + total sentence characters)', space: 'O(total root characters) — the trie' },
      },
    ],
  },
  {
    id: 'word-search-ii-drill',
    title: 'Word Search II (Trie + Board Backtracking)',
    level: 'Advanced',
    crossReference:
      "This drill's full Basic vs. Optimized walkthrough lives in this pattern's curated problem list — open " +
      "\"Word Search II\" there for the complete step-by-step and both code versions. It combines everything in " +
      "this pattern with the Backtracking pattern group that comes later in the course.",
  },
  {
    id: 'maximum-xor-of-two-numbers',
    title: 'Maximum XOR of Two Numbers in an Array (Binary Trie)',
    level: 'Advanced',
    versions: [
      {
        name: 'Basic — Check Every Pair',
        explanation: 'Compute the XOR of every possible pair of numbers directly, and keep the largest result. Correct and simple, but every pair is checked even though most contribute nothing useful once a strong candidate is already found.',
        steps: [
          'For each pair of numbers, compute their XOR directly.',
          'Track the maximum XOR value seen across every pair.',
          'Every pair is checked — O(n²).',
        ],
        code:
`int findMaximumXORBasic(int[] nums) {
    int maxXor = 0;
    for (int i = 0; i < nums.length; i++) {
        for (int j = i + 1; j < nums.length; j++) {
            maxXor = Math.max(maxXor, nums[i] ^ nums[j]);
        }
    }
    return maxXor;
}`,
        complexity: { time: 'O(n²)', space: 'O(1)' },
      },
      {
        name: 'Optimized — A Binary Trie of Bit Patterns',
        explanation: 'A trie doesn\'t have to be built from characters — it can be built from BITS, with just two possible "letters," 0 and 1, at every level. Insert every number\'s binary representation (most significant bit first). Then for each number, greedily walk the trie trying to take the OPPOSITE bit at every level — that greedy choice maximizes XOR one bit at a time, from most significant (most valuable) down to least.',
        steps: [
          'Insert every number\'s bits (MSB to LSB) into a binary trie.',
          'For each number, walk the trie again: at every level, try to go to the child representing the OPPOSITE of this number\'s own bit at that position.',
          'If that opposite branch exists, take it (this bit position contributes to the XOR) — otherwise, take the only branch available (same bit, contributes nothing at this position).',
          'The value assembled by this greedy walk is the best possible XOR partner for this number — track the max across all numbers.',
        ],
        visual: {
          trie: { label: 'binary trie holding 5\'s and 25\'s bit-paths (the pair that wins) — 5-bit, MSB first', value: {
            isEnd: false,
            children: {
              '0': { isEnd: false, children: { '0': { isEnd: false, children: { '1': { isEnd: false, children: { '0': { isEnd: false, children: { '1': { isEnd: true, children: {} } } } } } } } } },
              '1': { isEnd: false, children: { '1': { isEnd: false, children: { '0': { isEnd: false, children: { '0': { isEnd: false, children: { '1': { isEnd: true, children: {} } } } } } } } } },
            },
          } },
          varNames: ['xorSoFar'],
          steps: [
            { highlights: { trie: ['1'] }, vars: { xorSoFar: '1____' }, note: 'Querying with 5 = 00101. Bit 4 (MSB) of 5 is 0 — want the opposite, 1. Only 25 has a 1 here — take that branch. XOR bit: 1.' },
            { highlights: { trie: ['11'] }, vars: { xorSoFar: '11___' }, note: 'Bit 3 of 5 is 0 — want 1. 25\'s bit 3 is 1 — matches. XOR bit: 1.' },
            { highlights: { trie: ['110'] }, vars: { xorSoFar: '111__' }, note: 'Bit 2 of 5 is 1 — want 0. 25\'s bit 2 is 0 — matches. XOR bit: 1.' },
            { highlights: { trie: ['1100'] }, vars: { xorSoFar: '1110_' }, note: 'Bit 1 of 5 is 0 — want 1, but 25\'s only child here is 0 (no opposite available) — forced to match. XOR bit: 0.' },
            { highlights: { trie: ['11001'] }, vars: { xorSoFar: '11100' }, note: 'Bit 0 of 5 is 1 — want 0, but 25\'s bit is 1 — forced to match. XOR bit: 0. Final: 11100₂ = 28 — exactly 5 XOR 25, the true maximum for this array.' },
          ],
        },
        code:
`int findMaximumXOR(int[] nums) {
    TrieNode root = new TrieNode();
    for (int num : nums) insertBits(root, num);

    int maxXor = 0;
    for (int num : nums) {
        maxXor = Math.max(maxXor, bestXorWith(root, num));
    }
    return maxXor;
}

private void insertBits(TrieNode root, int num) {
    TrieNode node = root;
    for (int bit = 31; bit >= 0; bit--) {
        int b = (num >> bit) & 1;
        node.children.putIfAbsent(b, new TrieNode());
        node = node.children.get(b);
    }
}

private int bestXorWith(TrieNode root, int num) {
    TrieNode node = root;
    int result = 0;
    for (int bit = 31; bit >= 0; bit--) {
        int b = (num >> bit) & 1;
        int wanted = 1 - b;   // the opposite bit maximizes XOR here
        if (node.children.containsKey(wanted)) {
            result |= (1 << bit);
            node = node.children.get(wanted);
        } else {
            node = node.children.get(b);
        }
    }
    return result;
}`,
        complexity: { time: 'O(32n) ≈ O(n) — 32 bits per number, insert and query both linear', space: 'O(32n) — the trie' },
        interviewNote: "A binary trie generalizes the whole pattern beyond text: any time \"maximize/minimize XOR against a set\" comes up, this bit-by-bit greedy trie walk is the standard O(n) technique, versus O(n²) checking every pair.",
        whenToUse: "Reach for this whenever a problem asks about XOR across many pairs — the greedy \"try the opposite bit\" walk is only possible because a trie lets you commit to a choice at each level and only explore what's actually there.",
      },
    ],
  },
];

export default trieDrills;
